import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Trash-style left control: decrease until min, then remove line (qty 0).
   * @returns {boolean}
   */
  #trashDecrements() {
    return this.hasAttribute('data-trash-decrements');
  }

  /**
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    if (!this.#trashDecrements()) {
      super.decreaseQuantity(event);
      return;
    }

    const { min, value } = this.getCurrentValues();

    if (value > min) {
      this.updateQuantity(-1);
      return;
    }

    const { quantityInput } = this.refs;
    quantityInput.value = '0';
    this.onQuantityChange();
  }

  /**
   * Trash vs minus icon on the left control (quantity above min → minus, else → trash).
   */
  #syncTrashMinusIcons() {
    if (!this.#trashDecrements()) return;

    const { minusButton } = this.refs;
    if (!(minusButton instanceof HTMLButtonElement)) return;

    const minusWrap = minusButton.querySelector('.quantity-minus__minus-icon');
    const trashWrap = minusButton.querySelector('.quantity-minus__trash-icon');
    if (!(minusWrap instanceof HTMLElement) || !(trashWrap instanceof HTMLElement)) return;

    const { min, value } = this.getCurrentValues();
    const showMinus = value > min;

    minusWrap.classList.toggle('hidden', !showMinus);
    trashWrap.classList.toggle('hidden', showMinus);

    const dec = minusButton.dataset.decreaseLabel;
    const trash = minusButton.dataset.trashLabel;
    if (dec && trash) {
      minusButton.setAttribute('aria-label', showMinus ? dec : trash);
    }
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) {
      if (this.#trashDecrements()) {
        const cannotRemoveLine = this.hasAttribute('data-cannot-remove-line');
        minusButton.disabled = Boolean(cannotRemoveLine && value <= min);
      } else {
        minusButton.disabled = value <= min;
      }
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }

    this.#syncTrashMinusIcons();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
